import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ShoppingCart, Loader2, Flame, Store, X, Search, Bitcoin, AlertTriangle, BadgeCheck, ChevronRight, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/cart/CartContext";
import { useAuth } from "@/auth/AuthContext";
import { FadeUp } from "@/components/Reveal";
import { useContent } from "@/hooks/useContent";
import { PRODUCTS } from "@/data/products";
import { warehouseTag, warehouseName } from "@/utils/warehouse";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_TITLES = { vial: "Single vials", bundle: "Bundles", case: "Full case" };

const RAIL_LABELS = { paypal: "PayPal", venmo: "Venmo", cashapp: "CashApp", zelle: "Zelle", btc: "Bitcoin", eth: "Ethereum", usdt: "USDT", other: "Other" };

function ProductCard({ p, i, ruo, trending, carriers, isMember, vendors, rails }) {
  const { add } = useCart();
  const cryptoRails = (rails?.[p.vendorId || "house"] || []).filter((r) => ["btc", "eth", "usdt"].includes(r));
  const variants = p.variants?.length
    ? p.variants
    : [{ id: "base", label: p.size, size: p.size, kind: "vial", price: p.livePrice, member_price: p.memberPrice, stock: p.liveStock }];
  const [vid, setVid] = useState(variants[0].id);
  const v = variants.find((x) => x.id === vid) || variants[0];
  const groups = ["vial", "bundle", "case"]
    .map((k) => [k, variants.filter((x) => (x.kind || "vial") === k)])
    .filter(([, list]) => list.length);
  const seller = vendors.find((s) => s.id === (p.vendorId || "house"));
  const sku = p.id.startsWith("v-") ? `V-${p.id.slice(2, 8).toUpperCase()}` : p.id.toUpperCase();

  return (
    <FadeUp delay={0.05}>
      <article
        id={`product-${p.id}`}
        data-testid={`product-card-${p.id}`}
        className="rounded-2xl border border-[#232B40] bg-paper overflow-hidden scroll-mt-28 flex flex-col hover:border-[rgba(200,169,106,0.45)] transition-colors duration-300"
      >
        {/* Product photo — VetForge logo is printed on the vial label itself */}
        <div className="relative bg-gradient-to-b from-[#1A233A] to-[#0D1322]">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(200,169,106,0.1),transparent_70%)] pointer-events-none z-10" aria-hidden="true" />
          <img
            src={p.image}
            alt={p.liveName}
            onError={(e) => { e.currentTarget.src = "/images/hero-vials.png"; }}
            className="w-full aspect-[4/3] object-cover"
            data-testid={`product-image-${p.id}`}
          />
          {p.coa && (
            <a
              href={`${API}/products/${p.id}/coa`}
              target="_blank"
              rel="noreferrer"
              data-testid={`coa-link-${p.id}`}
              className="absolute top-4 left-4 z-20 inline-flex items-center gap-1.5 bg-[#0D1322]/85 backdrop-blur border border-[#22c55e]/40 text-[#22c55e] text-[10px] uppercase tracking-[0.2em] font-semibold px-3 py-1.5 rounded-full hover:bg-[#0D1322] transition-colors duration-300"
            >
              <BadgeCheck className="h-3.5 w-3.5" /> View COA
            </a>
          )}
          {trending && (
            <span className="absolute top-4 right-4 z-20 inline-flex items-center gap-1.5 bg-[#0D1322]/85 backdrop-blur text-clinical border border-clinical/40 text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 rounded-full" data-testid={`trending-badge-${p.id}`}>
              <Flame className="h-3 w-3" /> Trending
            </span>
          )}
        </div>

        <div className="p-6 sm:p-7 space-y-5 flex flex-col flex-1">
          <div className="space-y-1.5">
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#8E96AB]" data-testid={`product-sku-${p.id}`}>{sku} — {p.category}</p>
            <h2 className="font-serif text-2xl tracking-tight text-ink" data-testid={`product-tag-${p.id}`}>{p.liveName}</h2>
            <Link
              to={`/products?vendor=${p.vendorId || "house"}`}
              data-testid={`vendor-badge-${p.id}`}
              className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.15em] text-[#B9BFCE] hover:text-clinical transition-colors duration-300"
            >
              <span className="inline-flex items-center justify-center h-5 w-5 rounded bg-clinical text-[#0B101C] font-stencil text-[9px] leading-none">{(seller?.monogram || "VF").slice(0, 2)}</span>
              {p.vendorName} <ChevronRight className="h-3 w-3" />
            </Link>
            {cryptoRails.length > 0 && (
              <Link
                to="/crypto-briefing"
                data-testid={`crypto-rails-${p.id}`}
                title="This store accepts crypto — see the Crypto Briefing for the lowest-fee route"
                className="inline-flex items-center gap-1.5 flex-wrap pt-1 group"
              >
                <Bitcoin className="h-3 w-3 text-clinical" strokeWidth={1.5} />
                <span className="text-[9px] uppercase tracking-[0.15em] text-[#8E96AB] group-hover:text-clinical transition-colors duration-300">Accepts</span>
                {cryptoRails.map((r) => (
                  <span
                    key={r}
                    data-testid={`crypto-rail-${p.id}-${r}`}
                    className="text-[9px] font-mono uppercase tracking-[0.1em] text-clinical border border-clinical/40 rounded px-1.5 py-0.5 group-hover:border-clinical transition-colors duration-300"
                  >
                    {r}
                  </span>
                ))}
              </Link>
            )}
            {p.promo && (
              <span
                data-testid={`promo-badge-${p.id}`}
                title={`${p.promo.name} — sale ends ${new Date(p.promo.ends_at).toLocaleDateString()}`}
                className="inline-flex items-center gap-1.5 w-fit text-[9px] uppercase tracking-[0.15em] font-semibold text-[#0B101C] bg-[#DBC28C] border border-clinical/50 rounded-full px-2.5 py-1 mt-1"
              >
                {p.promo.name}{p.promo.kind === "percent" ? ` −${p.promo.percent}%` : " · Sale"}
              </span>
            )}
            {p.description && <p className="text-xs text-[#8E96AB] leading-relaxed pt-1">{p.description}</p>}
          </div>

          <div className="space-y-3" data-testid={`variant-selector-${p.id}`}>
            {groups.map(([kind, list]) => (
              <div key={kind} className="space-y-2">
                {groups.length > 1 && <p className="text-[10px] uppercase tracking-[0.25em] text-[#8E96AB]">{KIND_TITLES[kind]}</p>}
                <div className="flex flex-wrap gap-2">
                  {list.map((opt) => (
                    <button
                      key={opt.id}
                      data-testid={`variant-${p.id}-${opt.id}`}
                      onClick={() => setVid(opt.id)}
                      className={`text-xs uppercase tracking-[0.12em] px-4 py-2.5 rounded-xl border transition-colors duration-300 flex items-center gap-2 ${
                        vid === opt.id
                          ? "border-clinical bg-clinical/10 text-clinical"
                          : "border-[#232B40] text-[#B9BFCE] hover:border-[rgba(200,169,106,0.5)] hover:text-ink"
                      }`}
                    >
                      {opt.label}
                      <span className={vid === opt.id ? "text-clinical" : "text-[#8E96AB]"}>${opt.price}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Per-store price comparison for the selected size */}
          {carriers && carriers.length > 1 && (
            <div className="border border-[#232B40] rounded-xl overflow-hidden" data-testid={`carried-by-${p.id}`}>
              <p className="px-4 py-2.5 text-[10px] uppercase tracking-[0.25em] text-[#8E96AB] bg-surface border-b border-[#232B40]">
                {carriers.length} stores · {(v.size || p.size || "").toUpperCase() || "THIS SIZE"}
              </p>
              {carriers.map((c) => (
                <a
                  key={c.id}
                  href={`#product-${c.id}`}
                  data-testid={`carried-by-${p.id}-${c.id}`}
                  className={`flex items-center justify-between gap-3 px-4 py-3 text-xs border-b border-[#232B40] last:border-b-0 transition-colors duration-300 ${c.id === p.id ? "bg-clinical/5" : "hover:bg-surface"}`}
                >
                  <span className={`flex items-center gap-2.5 uppercase tracking-[0.15em] ${c.id === p.id ? "text-clinical" : "text-[#B9BFCE]"}`}>
                    <span className="inline-flex items-center justify-center h-5 w-5 rounded bg-clinical text-[#0B101C] font-stencil text-[9px] leading-none">{(c.vendorName || "VF").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()}</span>
                    {c.vendorName}{c.id === p.id ? " — this store" : ""}
                    {c.best && (
                      <span className="inline-flex items-center text-[9px] font-semibold uppercase tracking-[0.15em] text-[#0B101C] bg-clinical rounded-full px-2 py-0.5" data-testid={`best-price-${p.id}-${c.id}`}>
                        Best price
                      </span>
                    )}
                  </span>
                  <span className="flex items-center gap-3 font-mono">
                    <span className="text-ink">from ${c.fromPrice}</span>
                    <span
                      className="not-italic font-sans text-[9px] uppercase tracking-[0.1em] text-[#8E96AB] border border-[#232B40] rounded px-1.5 py-0.5"
                      title={`Ships from ${warehouseName(c.warehouse)}`}
                      data-testid={`warehouse-tag-${p.id}-${c.id}`}
                    >
                      {warehouseTag(c.warehouse)}
                    </span>
                    {c.memberFrom != null && (
                      <span className="text-clinical" data-testid={`carried-member-${p.id}-${c.id}`}>member ${c.memberFrom}</span>
                    )}
                  </span>
                </a>
              ))}
            </div>
          )}

          <div className="mt-auto space-y-4 pt-1">
            {v.member_price && isMember ? (
              <div data-testid={`product-price-${p.id}`}>
                <p className="font-serif text-3xl text-clinical flex items-center gap-2.5">
                  ${v.member_price} <span className="text-lg line-through text-[#8E96AB]">${p.originalPrice ?? v.price}</span>
                  <span
                    className="font-sans text-[9px] uppercase tracking-[0.1em] text-[#8E96AB] border border-[#232B40] rounded px-1.5 py-0.5 no-underline"
                    title={`Ships from ${warehouseName(p.warehouse)}`}
                    data-testid={`warehouse-tag-${p.id}`}
                  >
                    {warehouseTag(p.warehouse)}
                  </span>
                </p>
                <p className="text-[10px] uppercase tracking-[0.25em] text-clinical mt-1" data-testid={`member-price-tag-${p.id}`}>Member price — applied automatically</p>
              </div>
            ) : (
              <div data-testid={`product-price-${p.id}`}>
                <p className="font-serif text-3xl text-clinical flex items-center gap-2.5">
                  ${v.price}
                  {p.promo && p.originalPrice != null && <span className="text-lg line-through text-[#8E96AB]">${p.originalPrice}</span>}
                  <span
                    className="font-sans text-[9px] uppercase tracking-[0.1em] text-[#8E96AB] border border-[#232B40] rounded px-1.5 py-0.5"
                    title={`Ships from ${warehouseName(p.warehouse)}`}
                    data-testid={`warehouse-tag-${p.id}`}
                  >
                    {warehouseTag(p.warehouse)}
                  </span>
                </p>
                {v.member_price && !isMember && (
                  <Link to="/membership" className="inline-block text-[10px] uppercase tracking-[0.25em] text-clinical mt-1 hover:underline" data-testid={`member-teaser-${p.id}`}>
                    Members pay ${v.member_price} — join to unlock
                  </Link>
                )}
              </div>
            )}
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <p className="text-[10px] uppercase tracking-[0.25em]" data-testid={`stock-${p.id}`}>
                {v.stock > 10 ? <span className="text-clinical">In stock — {v.stock}</span> : v.stock > 0 ? <span className="text-warning">Low stock — {v.stock} left</span> : <span className="text-[#EF4444]">Out of stock</span>}
              </p>
              <p className="text-[9px] uppercase tracking-[0.2em] text-warning" data-testid={`product-warning-${p.id}`}>{ruo}</p>
            </div>
            <button
              data-testid={`add-to-cart-${p.id}`}
              onClick={() => { add(p, v); toast.success(`${p.liveName} — ${v.label} added to cart`); }}
              disabled={v.stock === 0}
              className="group w-full inline-flex items-center justify-center gap-3 bg-clinical text-[#0B101C] text-xs uppercase tracking-[0.2em] font-semibold py-4 px-8 rounded-xl hover:bg-[#DBC28C] transition-colors duration-300 disabled:opacity-40 disabled:hover:bg-clinical disabled:cursor-not-allowed"
            >
              {v.stock === 0 ? "Out of stock" : "Add to cart"}
              <ShoppingCart className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </div>
        </div>
      </article>
    </FadeUp>
  );
}

export default function Products() {
  const content = useContent();
  const { token, membership } = useAuth();
  const isMember = membership?.membership === "active";
  const COPY = {
    overline: "Catalog",
    title1: "Every vial,",
    title2: "accounted for.",
    intro: "Lyophilized peptides and USP-grade diluents, each released against an independent Certificate of Analysis. Pricing and availability are provided on inquiry.",
    ...(content?.products_page || {}),
  };
  const RUO = content?.footer?.ruo || "Research use only — Not for human consumption";
  const [catalog, setCatalog] = useState(null);
  const [failed, setFailed] = useState(false);
  const [popularity, setPopularity] = useState({});
  const [vendors, setVendors] = useState([]);
  const [rails, setRails] = useState({}); // vendorKey -> [rail types]
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState(() => searchParams.get("q") || "");
  const [catFilter, setCatFilter] = useState("all"); // all | peptide | longevity | new | coa
  const [payFilter, setPayFilter] = useState("");
  const [cryptoNotice, setCryptoNotice] = useState(() => !localStorage.getItem("vf-crypto-notice-dismissed"));
  const vendorFilter = searchParams.get("vendor") || "";
  const [recent, setRecent] = useState(() => {
    try { return JSON.parse(localStorage.getItem("vf-recent")) || []; } catch { return []; }
  });

  useEffect(() => {
    const q = searchParams.get("q");
    if (q) setQuery(q);
  }, [searchParams]);

  useEffect(() => {
    axios.get(`${API}/products`).then(({ data }) => setCatalog(data)).catch(() => setFailed(true));
    axios.get(`${API}/products/popularity`).then(({ data }) => setPopularity(data)).catch(() => {});
    axios.get(`${API}/vendors`).then(({ data }) => setVendors(data)).catch(() => {});
  }, []);

  // Fetch each store's accepted payment rails for the payment-method filter
  useEffect(() => {
    if (!vendors.length) return;
    vendors.forEach((v) => {
      if (rails[v.id]) return;
      axios.get(`${API}/vendors/${v.id}/payment-options`).then(({ data }) => {
        setRails((prev) => ({ ...prev, [v.id]: (data.options || []).map((o) => o.type) }));
      }).catch(() => setRails((prev) => ({ ...prev, [v.id]: [] })));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendors]);

  useEffect(() => {
    if (catalog && window.location.hash) {
      const el = document.getElementById(window.location.hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    }
  }, [catalog]);

  // Count a product view each time a card scrolls into view (once per card per page visit)
  useEffect(() => {
    if (!catalog) return;
    const seen = new Set();
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (en.isIntersecting && !seen.has(en.target.id)) {
          seen.add(en.target.id);
          const pid = en.target.id.replace("product-", "");
          axios.post(
            `${API}/track`,
            { kind: "product_view", product_id: pid },
            token ? { headers: { Authorization: `Bearer ${token}` } } : {}
          ).catch(() => {});
          setRecent((prev) => {
            const next = [pid, ...prev.filter((x) => x !== pid)].slice(0, 6);
            localStorage.setItem("vf-recent", JSON.stringify(next));
            return next;
          });
        }
      });
    }, { threshold: 0.4 });
    document.querySelectorAll("[id^='product-']").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [catalog, token]);

  const activeVendor = vendorFilter ? vendors.find((v) => v.id === vendorFilter) : null;
  const allRails = [...new Set(Object.values(rails).flat())];

  const ITEMS = (catalog || []).map((c) => {
    const st = PRODUCTS.find((p) => p.id === c.id) || {};
    return {
      ...st,
      id: c.id,
      liveName: c.name,
      livePrice: c.price,
      liveStock: c.stock,
      size: c.size || st.size,
      form: c.form || st.form,
      description: c.description || st.description,
      image: c.image || st.image || `/images/${c.id}.png`,
      category: st.category || "Peptide",
      coa: c.coa || false,
      variants: c.variants || null,
      memberPrice: c.member_price ?? null,
      promo: c.promo || null,
      originalPrice: c.original_price ?? null,
      vendorId: c.vendor_id || null,
      vendorName: c.vendor_name || "VetForge Supply",
      warehouse: c.warehouse || "us",
      isNew: !!c.vendor_id, // marketplace arrivals from vendor stores
    };
  }).filter((p) => !vendorFilter || (vendorFilter === "house" ? !p.vendorId : p.vendorId === vendorFilter))
    .filter((p) => {
      const q = query.trim().toLowerCase();
      if (!q) return true;
      return (p.liveName || "").toLowerCase().includes(q) || (p.description || "").toLowerCase().includes(q);
    })
    .filter((p) => {
      if (catFilter === "coa") return p.coa;
      if (catFilter === "new") return p.isNew;
      if (catFilter === "us") return (p.warehouse || "us") === "us";
      if (catFilter === "peptide") return (p.category || "").toLowerCase().includes("peptide");
      if (catFilter === "longevity") return (p.category || "").toLowerCase().includes("longevity");
      return true;
    })
    .filter((p) => {
      if (!payFilter) return true;
      return (rails[p.vendorId || "house"] || []).includes(payFilter);
    })
    .sort((a, b) => (popularity[b.id] || 0) - (popularity[a.id] || 0));
  const trendingId = ITEMS.length && (popularity[ITEMS[0].id] || 0) > 0 ? ITEMS[0].id : null;
  const recentItems = recent.map((id) => ITEMS.find((p) => p.id === id)).filter(Boolean).slice(0, 5);
  const newCount = (catalog || []).filter((c) => c.vendor_id).length;

  // Cross-vendor comparison: group the FULL catalog (unfiltered) by normalized product name + vendor,
  // so duplicate listings from the SAME store collapse to one row and a strip only appears
  // when at least two DIFFERENT stores carry the product
  const nameGroups = {};
  (catalog || []).forEach((c) => {
    const key = (c.name || "").toLowerCase().trim();
    if (!key) return;
    const g = (nameGroups[key] = nameGroups[key] || {});
    const vkey = c.vendor_id || "house";
    (g[vkey] = g[vkey] || []).push(c);
  });
  const carriersFor = (p) => {
    if (vendorFilter) return null; // single-store view — comparison strip only makes sense in all-stores mode
    const vendorMap = nameGroups[(p.liveName || "").toLowerCase().trim()] || {};
    const vendorEntries = Object.values(vendorMap);
    if (vendorEntries.length < 2) return null;
    const rows = vendorEntries.map((entries) => {
      const vars = entries.flatMap((c) => c.variants?.length ? c.variants : [{ price: c.price, member_price: c.member_price }]);
      const memberPrices = vars.map((x) => x.member_price).filter((x) => x != null);
      const fromPrice = Math.min(...vars.map((x) => x.price));
      const memberFrom = memberPrices.length ? Math.min(...memberPrices) : null;
      return {
        id: entries[0].id,
        vendorName: entries[0].vendor_name || "VetForge Supply",
        warehouse: entries[0].warehouse || "us",
        fromPrice,
        memberFrom,
        effective: memberFrom ?? fromPrice,
      };
    }).sort((a, b) => a.effective - b.effective);
    rows[0].best = true;
    return rows;
  };

  const FILTER_CHIPS = [
    ["all", "All"],
    ["peptide", "Peptides"],
    ["longevity", "Longevity"],
    ["new", "New Arrivals"],
    ["coa", "COA only"],
    ["us", "US only"],
  ];

  return (
    <div data-testid="products-page">
      <section className="mx-auto max-w-7xl px-6 sm:px-10 pt-24 pb-14 lg:pt-28">
        {/* Notice cards — crypto savings + RUO, matching the marketplace shop pattern */}
        {cryptoNotice && (
          <FadeUp>
            <div className="rounded-2xl border border-[rgba(200,169,106,0.35)] bg-paper p-6 sm:p-7 mb-4 relative" data-testid="crypto-notice">
              <button
                onClick={() => { setCryptoNotice(false); localStorage.setItem("vf-crypto-notice-dismissed", "1"); }}
                aria-label="Dismiss notice"
                data-testid="crypto-notice-dismiss"
                className="absolute top-4 right-4 text-[#8E96AB] hover:text-ink transition-colors duration-300"
              >
                <X className="h-4 w-4" />
              </button>
              <div className="flex items-start gap-4">
                <span className="inline-flex items-center justify-center h-11 w-11 rounded-full border border-[rgba(200,169,106,0.45)] text-clinical shrink-0">
                  <Bitcoin className="h-5 w-5" strokeWidth={1.5} />
                </span>
                <div>
                  <p className="font-serif text-xl text-ink tracking-tight">Paying with crypto? You could pay less.</p>
                  <p className="text-sm text-[#B9BFCE] leading-relaxed mt-1.5 max-w-2xl">
                    On many items, paying with a crypto method matches the lowest seller's price — the saving is applied automatically at checkout. Every store lists its own accepted rails.
                  </p>
                </div>
              </div>
            </div>
          </FadeUp>
        )}
        <FadeUp delay={0.03}>
          <div className="rounded-2xl border border-[#232B40] bg-paper p-6 sm:p-7 flex items-start gap-4" data-testid="ruo-notice">
            <AlertTriangle className="h-6 w-6 text-warning shrink-0 mt-0.5" strokeWidth={1.5} />
            <div>
              <p className="font-serif text-lg text-warning tracking-tight">For research purposes only. Not for human consumption.</p>
              <p className="text-sm text-[#B9BFCE] leading-relaxed mt-1">Every compound is sold to verified researchers 21+ and ships with its batch documentation.</p>
            </div>
          </div>
        </FadeUp>

        {/* Search bar — compounds, SKUs, stores */}
        <FadeUp delay={0.05}>
          <form
            onSubmit={(e) => e.preventDefault()}
            className="mt-8 flex items-center gap-2 rounded-2xl border border-[#232B40] bg-paper p-2 pl-5 max-w-2xl"
            data-testid="product-search-wrap"
          >
            <Search className="h-4 w-4 text-[#8E96AB] shrink-0" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search compounds or SKUs…"
              aria-label="Search products"
              data-testid="product-search"
              className="flex-1 bg-transparent text-sm text-ink placeholder:text-[#8E96AB] focus:outline-none min-w-0"
            />
            {query && (
              <button type="button" onClick={() => setQuery("")} aria-label="Clear search" data-testid="product-search-clear" className="text-[#8E96AB] hover:text-clinical transition-colors duration-300">
                <X className="h-4 w-4" />
              </button>
            )}
            <span className="bg-clinical text-[#0B101C] text-[10px] uppercase tracking-[0.2em] font-semibold rounded-xl px-5 py-3" data-testid="product-search-button">Search</span>
          </form>
          {query.trim() && (
            <p className="mt-3 text-[10px] uppercase tracking-[0.25em] text-[#8E96AB]" data-testid="product-search-count">
              {ITEMS.length} result{ITEMS.length === 1 ? "" : "s"} for "{query.trim()}"
            </p>
          )}
        </FadeUp>

        {/* Filters — category chips + payment-method dropdown */}
        <FadeUp delay={0.06}>
          <div className="mt-8 space-y-4" data-testid="shop-filters">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#8E96AB]">Filter</p>
            <div className="flex flex-wrap items-center gap-2">
              {FILTER_CHIPS.map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setCatFilter(key)}
                  data-testid={`filter-${key}`}
                  className={`text-[10px] uppercase tracking-[0.15em] px-4 py-2 rounded-full border transition-colors duration-300 inline-flex items-center gap-1.5 ${
                    catFilter === key ? "border-clinical bg-clinical text-[#0B101C] font-semibold" : "border-[#232B40] text-[#B9BFCE] hover:border-[rgba(200,169,106,0.5)]"
                  }`}
                >
                  {key === "new" && <Sparkles className="h-3 w-3" />}
                  {key === "coa" && <BadgeCheck className="h-3 w-3" />}
                  {key === "us" && <span className="font-mono text-[9px] tracking-[0.1em]">US</span>}
                  {label}
                </button>
              ))}
              <select
                value={payFilter}
                onChange={(e) => setPayFilter(e.target.value)}
                data-testid="filter-payment"
                className="ml-auto border border-[#232B40] bg-[#0B101C] text-[#B9BFCE] text-[10px] uppercase tracking-[0.15em] h-9 px-3 rounded-full focus:outline-none focus:ring-1 focus:ring-clinical"
              >
                <option value="">All payment methods</option>
                {allRails.map((r) => (
                  <option key={r} value={r}>{RAIL_LABELS[r] || r}</option>
                ))}
              </select>
            </div>
          </div>
        </FadeUp>

        {/* Shop by seller */}
        {vendors.length > 1 && !vendorFilter && (
          <FadeUp delay={0.08}>
            <div className="mt-10 space-y-3" data-testid="shop-by-seller">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#8E96AB]">Shop by seller</p>
              <div className="space-y-2" data-testid="vendor-filter">
                <Link
                  to="/products"
                  data-testid="vendor-filter-all"
                  className={`flex items-center gap-3 rounded-xl border px-4 py-3.5 transition-colors duration-300 ${!vendorFilter ? "border-clinical bg-clinical/10" : "border-[#232B40] hover:border-[rgba(200,169,106,0.5)]"}`}
                >
                  <span className="inline-flex items-center justify-center h-8 w-8 rounded-lg bg-clinical text-[#0B101C] font-stencil text-xs">VF</span>
                  <span className={`text-sm ${!vendorFilter ? "text-clinical" : "text-ink"}`}>All stores</span>
                  <ChevronRight className="h-4 w-4 text-[#8E96AB] ml-auto" />
                </Link>
                {vendors.map((v) => (
                  <Link
                    key={v.id}
                    to={`/products?vendor=${v.id}`}
                    data-testid={`vendor-filter-${v.id === "house" ? "house" : v.id.slice(0, 8)}`}
                    className="flex items-center gap-3 rounded-xl border border-[#232B40] px-4 py-3.5 hover:border-[rgba(200,169,106,0.5)] transition-colors duration-300"
                  >
                    {v.has_logo ? (
                      <img src={`${API}/vendors/${v.id}/branding/logo`} alt="" className="h-8 w-8 rounded-lg object-cover" />
                    ) : (
                      <span className="inline-flex items-center justify-center h-8 w-8 rounded-lg bg-clinical text-[#0B101C] font-stencil text-xs">{v.monogram}</span>
                    )}
                    <span className="text-sm text-ink">{v.name}</span>
                    <span className="text-[10px] text-[#8E96AB] ml-1">{v.product_count} products</span>
                    <ChevronRight className="h-4 w-4 text-[#8E96AB] ml-auto" />
                  </Link>
                ))}
              </div>
            </div>
          </FadeUp>
        )}

        {/* New arrivals banner */}
        {newCount > 0 && !vendorFilter && catFilter !== "new" && (
          <FadeUp delay={0.09}>
            <div className="mt-8 rounded-2xl border border-[#232B40] bg-paper p-6 flex flex-wrap items-center gap-5" data-testid="new-arrivals-banner">
              <div className="flex-1 min-w-[200px] space-y-1.5">
                <p className="font-serif text-xl text-ink tracking-tight flex items-center gap-2">
                  New arrivals <span className="text-clinical text-sm font-mono">{newCount}</span>
                </p>
                <p className="text-sm text-[#B9BFCE]">Freshly added by our vendor stores — see what just dropped.</p>
              </div>
              <button onClick={() => setCatFilter("new")} data-testid="view-new-arrivals" className="bg-clinical text-[#0B101C] text-[10px] uppercase tracking-[0.2em] font-semibold rounded-xl px-5 py-3 hover:bg-[#DBC28C] transition-colors duration-300">
                View new arrivals
              </button>
            </div>
          </FadeUp>
        )}

        {/* Active store banner (filtered view) */}
        {activeVendor && (
          <FadeUp delay={0.08}>
            <div className="mt-8 rounded-2xl border border-[rgba(200,169,106,0.35)] bg-paper p-6 flex flex-wrap items-center gap-5 relative overflow-hidden" data-testid="vendor-filter-banner">
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(200,169,106,0.1),transparent_65%)] pointer-events-none" aria-hidden="true" />
              {activeVendor.has_banner && (
                <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
                  <img src={`${API}/vendors/${activeVendor.id}/branding/banner`} alt="" className="w-full h-full object-cover opacity-20" data-testid="vendor-banner-bg" />
                  <div className="absolute inset-0 bg-gradient-to-r from-[#141B2E] via-[#141B2E]/80 to-transparent" />
                </div>
              )}
              {activeVendor.has_logo ? (
                <img src={`${API}/vendors/${activeVendor.id}/branding/logo`} alt={`${activeVendor.name} logo`} className="h-16 w-16 rounded-lg object-cover border border-[rgba(200,169,106,0.35)] relative" data-testid="vendor-banner-logo" />
              ) : (
                <span className="font-stencil text-xl leading-none text-[#0B101C] bg-clinical px-3 py-2 rounded-lg relative">{activeVendor.monogram}</span>
              )}
              <div className="flex-1 min-w-[200px] relative">
                <p className="font-serif text-2xl text-ink tracking-tight">{activeVendor.name}</p>
                {activeVendor.tagline && <p className="text-sm text-[#B9BFCE] mt-1">{activeVendor.tagline}</p>}
                {activeVendor.bio && <p className="text-xs text-[#8E96AB] mt-1 leading-relaxed">{activeVendor.bio}</p>}
              </div>
              <Link to="/products" data-testid="vendor-filter-clear" className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[#8E96AB] hover:text-clinical border border-[#232B40] rounded-full px-4 py-2 transition-colors duration-300 relative">
                <X className="h-3 w-3" /> All stores
              </Link>
            </div>
          </FadeUp>
        )}

        {/* Catalog index + recently viewed */}
        {catalog && ITEMS.length > 0 && (
          <FadeUp delay={0.1}>
            <nav className="mt-12 pt-8 border-t border-[#232B40] flex flex-wrap gap-x-8 gap-y-3" data-testid="catalog-index" aria-label="Catalog index">
              {ITEMS.map((p, i) => (
                <a
                  key={p.id}
                  href={`#product-${p.id}`}
                  data-testid={`catalog-index-link-${p.id}`}
                  className="group inline-flex items-baseline gap-2 text-xs uppercase tracking-[0.15em] text-[#B9BFCE] hover:text-clinical transition-colors duration-300"
                >
                  <span className="font-mono text-[10px] text-[#8E96AB] group-hover:text-clinical transition-colors duration-300">{String(i + 1).padStart(2, "0")}</span>
                  <span className="underline-offset-4 group-hover:underline">{p.liveName}</span>
                </a>
              ))}
            </nav>
            {recentItems.length > 0 && (
              <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2" data-testid="recently-viewed">
                <span className="text-[10px] uppercase tracking-[0.25em] text-[#8E96AB]">Recently viewed</span>
                {recentItems.map((p) => (
                  <a
                    key={p.id}
                    href={`#product-${p.id}`}
                    data-testid={`recent-link-${p.id}`}
                    className="text-[11px] uppercase tracking-[0.15em] text-clinical hover:text-ink transition-colors duration-300 underline-offset-4 hover:underline"
                  >
                    {p.liveName}
                  </a>
                ))}
              </div>
            )}
          </FadeUp>
        )}
      </section>

      <section className="mx-auto max-w-7xl px-6 sm:px-10 pb-32">
        {catalog === null && !failed && (
          <div className="flex items-center gap-3 text-[#8E96AB] text-xs uppercase tracking-[0.25em]" data-testid="products-loading">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading the catalog
          </div>
        )}
        {failed && (
          <p className="text-xs uppercase tracking-[0.25em] text-[#EF4444]" data-testid="products-error">
            The catalog could not be loaded. Please refresh the page.
          </p>
        )}
        {catalog && ITEMS.length === 0 && (
          <p className="text-xs uppercase tracking-[0.25em] text-[#8E96AB]" data-testid="products-empty">
            {query.trim() ? `Nothing matches "${query.trim()}" — try a shorter name or clear the search.` : "Nothing matches these filters — try clearing them."}
          </p>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {ITEMS.map((p, i) => <ProductCard key={p.id} p={p} i={i} ruo={RUO} trending={p.id === trendingId} carriers={carriersFor(p)} isMember={isMember} vendors={vendors} rails={rails} />)}
        </div>
      </section>
    </div>
  );
}
